// Input validation utilities
import { LIMITS } from '../config';

export const validateInput = {
  // Sanitize user input to prevent XSS
  sanitizeString: (input) => {
    if (typeof input !== 'string') return '';
    return input
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .trim();
  },

  // Validate message length and content
  validateMessage: (message) => {
    if (!message || typeof message !== 'string') {
      return { isValid: false, error: 'Message is required' };
    }

    const sanitized = validateInput.sanitizeString(message);

    if (sanitized.length === 0) {
      return { isValid: false, error: 'Message cannot be empty' };
    }

    if (sanitized.length > LIMITS.maxMessageLength) {
      return { isValid: false, error: `Message cannot exceed ${LIMITS.maxMessageLength} characters` };
    }

    // Check for potentially harmful patterns
    const harmfulPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /eval\s*\(/i,
      /expression\s*\(/i,
    ];

    for (const pattern of harmfulPatterns) {
      if (pattern.test(sanitized)) {
        return { isValid: false, error: 'Message contains potentially harmful content' };
      }
    }

    return { isValid: true, sanitized };
  },

  // Validate username
  validateUsername: (username) => {
    if (!username || typeof username !== 'string') {
      return { isValid: false, error: 'Username is required' };
    }

    const sanitized = validateInput.sanitizeString(username);

    if (sanitized.length === 0) {
      return { isValid: false, error: 'Username cannot be empty' };
    }

    if (sanitized.length > LIMITS.maxUsernameLength) {
      return { isValid: false, error: `Username cannot exceed ${LIMITS.maxUsernameLength} characters` };
    }

    // Allow only alphanumeric characters, spaces, and common punctuation
    if (!/^[a-zA-Z0-9\s\-_.]+$/.test(sanitized)) {
      return { isValid: false, error: 'Username contains invalid characters' };
    }

    return { isValid: true, sanitized };
  },
};

// Rate limiting utility
export const rateLimiter = {
  requests: new Map(),

  isAllowed: (identifier, maxRequests = LIMITS.rateLimitRequests, windowMs = LIMITS.rateLimitWindowMs) => {
    const now = Date.now();
    const windowStart = now - windowMs;

    if (!rateLimiter.requests.has(identifier)) {
      rateLimiter.requests.set(identifier, []);
    }

    const userRequests = rateLimiter.requests.get(identifier);
    const validRequests = userRequests.filter((time) => time > windowStart);
    rateLimiter.requests.set(identifier, validRequests);

    if (validRequests.length >= maxRequests) {
      return false;
    }

    validRequests.push(now);
    return true;
  },

  // Clean up old entries periodically
  cleanup: () => {
    const now = Date.now();
    const maxAge = 300000; // 5 minutes

    for (const [identifier, requests] of rateLimiter.requests.entries()) {
      const validRequests = requests.filter((time) => now - time < maxAge);
      if (validRequests.length === 0) {
        rateLimiter.requests.delete(identifier);
      } else {
        rateLimiter.requests.set(identifier, validRequests);
      }
    }
  },
};

// Set up periodic cleanup
setInterval(rateLimiter.cleanup, 60000);
